import type { EditorContext } from "../tools/EditorContext";
import type { SceneSelection } from "../canvas/Scene";
import type paper from "paper";
import {
  descendantConstructionLineIds,
  projectConstructionGroups,
  projectTracingImages,
  type ConstructionGroup,
  type Project,
} from "./Project";
import { descendantLeafIds } from "./ConstructionObject";
import { computeDragClosure } from "./dragClosure";
import { propagateCoincidentAfterGroupMove } from "./constraintEnforcement";
import { addPieceLayoutDelta } from "./pieceTransform";
import {
  translatePieceInProject,
  translateHoleInProject,
  translateCutoutInProject,
  translateStitchLineInProject,
  translateDecorationInProject,
  translateConstructionLineInProject,
  translateClosureInProject,
} from "../tools/SelectTool";

/** True when the selection is something `nudgeSelection` can move — the
 *  same set `deleteSelection` acts on. Drives the arrow-key handler's
 *  early-out so a keypress with nothing movable falls through. */
export function hasNudgeableSelection(sel: SceneSelection): boolean {
  return (
    sel.pieceId != null ||
    (sel.pieceIds?.length ?? 0) > 0 ||
    sel.decorationId != null ||
    sel.holeId != null ||
    sel.cutoutId != null ||
    sel.stitchId != null ||
    sel.constructionId != null ||
    (sel.constructionIds?.length ?? 0) > 0 ||
    (sel.constructionGroupIds?.length ?? 0) > 0 ||
    (sel.tracingImageIds?.length ?? 0) > 0
  );
}

/** Translate the current selection by `(dx, dy)` project-mm in one history
 *  commit — the keyboard-nudge counterpart of a drag. Mirrors
 *  `deleteSelection`'s per-type priority chain and reuses the exact move
 *  ops the drag-commit uses, so nudge and drag stay in lock-step
 *  (linked-part cut-layout, construction closure + coincident constraints,
 *  group pivots, tracing images). Returns true when something moved. */
export function nudgeSelection(
  ctx: EditorContext,
  dx: number,
  dy: number,
): boolean {
  if (dx === 0 && dy === 0) return false;
  const sel = ctx.scene.getSelection();
  const scope = ctx.scene.getPaperScope();

  if (sel.decorationId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const decorationId = sel.decorationId;
    ctx.commit("Move decoration", (proj) =>
      translateDecorationInProject(proj, pieceId, decorationId, dx, dy, scope),
    );
    return true;
  }
  if (sel.holeId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const holeId = sel.holeId;
    ctx.commit("Move hole", (proj) =>
      translateHoleInProject(proj, pieceId, holeId, dx, dy),
    );
    return true;
  }
  if (sel.cutoutId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const cutoutId = sel.cutoutId;
    ctx.commit("Move cutout", (proj) =>
      translateCutoutInProject(proj, pieceId, cutoutId, dx, dy, scope),
    );
    return true;
  }
  if (sel.stitchId && sel.pieceId) {
    const pieceId = sel.pieceId;
    const stitchId = sel.stitchId;
    ctx.commit("Move stitch line", (proj) =>
      translateStitchLineInProject(proj, pieceId, stitchId, dx, dy, scope),
    );
    return true;
  }
  if (
    sel.constructionId ||
    (sel.constructionIds?.length ?? 0) > 0 ||
    (sel.constructionGroupIds?.length ?? 0) > 0 ||
    (sel.tracingImageIds?.length ?? 0) > 0
  ) {
    ctx.commit("Move construction selection", (proj) =>
      translateConstructionSelection(proj, sel, dx, dy, scope),
    );
    return true;
  }
  if (sel.pieceId) {
    const pieceIds =
      sel.pieceIds && sel.pieceIds.length > 0 ? sel.pieceIds : [sel.pieceId];
    // A linked part (constructionOutline) can't have its geometry rewritten —
    // its move accumulates into the cut-layout transform in Spread view, and
    // is locked in Assembled view (mirrors the drag path, #652).
    const spread = ctx.scene.getPartLayoutView() === "spread";
    ctx.commit("Move part", (proj) => {
      let next = proj;
      for (const id of pieceIds) {
        const piece = next.pieces.find((p) => p.id === id);
        if (piece?.constructionOutline) {
          if (!spread) continue;
          next = {
            ...next,
            pieces: next.pieces.map((p) =>
              p.id === id ? addPieceLayoutDelta(p, dx, dy) : p,
            ),
          };
        } else {
          next = translatePieceInProject(next, id, dx, dy, scope);
        }
      }
      return next;
    });
    return true;
  }
  return false;
}

/** Translate a construction selection (lines + groups + tracing images) by
 *  `(dx, dy)` — the same reducer shape as `ConstructionTool`'s group-move
 *  commit: expand groups to member lines/images, translate each member plus
 *  its snap-chain/offset closure, re-satisfy coincident constraints, and
 *  shift group / image pivots that moved as a unit. */
function translateConstructionSelection(
  proj: Project,
  sel: SceneSelection,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  const explicitLineIds =
    sel.constructionIds && sel.constructionIds.length > 0
      ? sel.constructionIds
      : sel.constructionId
        ? [sel.constructionId]
        : [];
  const lineIdSet = new Set<string>(explicitLineIds);
  const imageIdSet = new Set<string>(sel.tracingImageIds ?? []);
  for (const gid of sel.constructionGroupIds ?? []) {
    const members = descendantLeafIds(proj, gid);
    for (const lid of members.lineIds) lineIdSet.add(lid);
    for (const iid of members.imageIds) imageIdSet.add(iid);
  }
  const memberIds = new Set(lineIdSet);
  // Snap-chain partners + offset descendants of the moved lines ride along.
  const closure = computeDragClosure(proj, [...lineIdSet]);

  let moved = proj;
  for (const lid of lineIdSet) {
    moved = translateConstructionLineInProject(moved, lid, dx, dy, scope);
  }
  const movedWithClosure = translateClosureInProject(
    moved,
    closure,
    memberIds,
    dx,
    dy,
    scope,
  );
  const enforced = propagateCoincidentAfterGroupMove(
    movedWithClosure,
    movedWithClosure.constructionLines ?? [],
    [...memberIds],
    scope,
  );
  // Shift the pivot of every group whose ENTIRE member set moved, so its
  // tilted bbox stays attached (mirrors the group-move commit).
  const groups = projectConstructionGroups(movedWithClosure).map((g) => {
    if (!g.rotationCenter && !g.pivotPoint) return g;
    const gm = descendantConstructionLineIds(movedWithClosure, g.id);
    if (gm.length === 0 || !gm.every((id) => memberIds.has(id))) return g;
    const next: ConstructionGroup = {
      ...g,
      rotationCenter: g.rotationCenter
        ? { x: g.rotationCenter.x + dx, y: g.rotationCenter.y + dy }
        : undefined,
      pivotPoint: g.pivotPoint
        ? { x: g.pivotPoint.x + dx, y: g.pivotPoint.y + dy }
        : undefined,
    };
    return next;
  });
  const tracingImages =
    imageIdSet.size > 0
      ? projectTracingImages(movedWithClosure).map((img) =>
          imageIdSet.has(img.id)
            ? {
                ...img,
                x: img.x + dx,
                y: img.y + dy,
                ...(img.pivotPoint
                  ? {
                      pivotPoint: {
                        x: img.pivotPoint.x + dx,
                        y: img.pivotPoint.y + dy,
                      },
                    }
                  : {}),
              }
            : img,
        )
      : movedWithClosure.tracingImages;

  return {
    ...movedWithClosure,
    constructionGroups: groups,
    constructionLines: enforced,
    tracingImages,
  };
}
